/* ============================================================
   Vitrine — theme.js
   Sticky header state, drawers (cart / search / menu / filters),
   product-card image carousels, collection deck, grid-density
   toggle, accordion tabs, quick-add, cart AJAX.
   No dependencies.
   ============================================================ */
(function () {
  'use strict';
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const money = (cents) =>
    (window.theme?.moneyFormat || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));

  /* ---------- Sticky / transparent header ---------- */
  function initHeader() {
    const header = $('.header');
    if (!header) return;
    const transparentAllowed = header.dataset.transparent === 'true';
    const setState = () => {
      const stuck = window.scrollY > 8;
      header.classList.toggle('is-stuck', stuck && !header.classList.contains('is-inner'));
      header.classList.toggle(
        'is-transparent',
        transparentAllowed && !stuck && !header.classList.contains('is-inner')
      );
    };
    setState();
    window.addEventListener('scroll', setState, { passive: true });
  }

  /* ---------- Generic drawer ---------- */
  function initDrawers() {
    const openLock = () => document.body.classList.add('cust-scroll-lock');
    const unlock = () => {
      if (!$('.drawer.is-open')) document.body.classList.remove('cust-scroll-lock');
    };
    $$('[data-drawer-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const d = document.getElementById(btn.dataset.drawerOpen);
        if (d) { d.classList.add('is-open'); openLock(); }
      });
    });
    $$('.drawer').forEach((d) => {
      d.addEventListener('click', (e) => {
        if (e.target.matches('.drawer__overlay, [data-drawer-close]') || e.target.closest('[data-drawer-close]')) {
          d.classList.remove('is-open');
          unlock();
        }
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { $$('.drawer.is-open').forEach((d) => d.classList.remove('is-open')); unlock(); }
    });
  }

  /* ---------- Mobile menu accordions ----------
     Opening/closing the menu itself is handled by initDrawers() — the
     toggle button uses [data-drawer-open="MobileMenu"] and the panel is a
     regular .drawer, same mechanism as cart/search/filters. This only
     wires up the expand/collapse of nested link groups inside it. */
  function initMobileMenuAccordions() {
    $$('.mobile-menu [data-acc-toggle]').forEach((h) =>
      h.addEventListener('click', () => h.parentElement.classList.toggle('is-open'))
    );
  }

  /* ---------- Product-card image carousel ---------- */
  function initCardCarousels(scope = document) {
    $$('.card__media', scope).forEach((media) => {
      const track = $('.card__track', media);
      if (!track || track.children.length < 2) return;
      const dots = $$('.card__dots button', media);
      const slides = track.children.length;
      const update = () => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        dots.forEach((d, di) => d.setAttribute('aria-current', di === i ? 'true' : 'false'));
      };
      track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      dots.forEach((d, di) =>
        d.addEventListener('click', () => track.scrollTo({ left: di * track.clientWidth, behavior: 'smooth' }))
      );
      const go = (dir) => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        const n = Math.max(0, Math.min(slides - 1, i + dir));
        track.scrollTo({ left: n * track.clientWidth, behavior: 'smooth' });
      };
      $('.card__nav .prev', media)?.addEventListener('click', (e) => { e.preventDefault(); go(-1); });
      $('.card__nav .next', media)?.addEventListener('click', (e) => { e.preventDefault(); go(1); });
    });
  }

  /* ---------- Collection deck ---------- */
  function initDeck() {
    $$('.deck').forEach((deck) => {
      const cards = $$('.deck__card', deck);
      if (cards.length < 2) return;
      let active = 0;
      const layers = ['0', '1', '2', '3'];
      const render = () => {
        cards.forEach((c, i) => {
          const rel = (i - active + cards.length) % cards.length;
          c.dataset.layer = rel < layers.length ? layers[rel] : 'hidden';
          c.setAttribute('aria-hidden', rel === 0 ? 'false' : 'true');
        });
      };
      render();
      const next = () => { active = (active + 1) % cards.length; render(); };
      let timer = setInterval(next, 3500);
      deck.addEventListener('pointerenter', () => clearInterval(timer));
      deck.addEventListener('pointerleave', () => (timer = setInterval(next, 3500)));
      cards.forEach((c, i) =>
        c.addEventListener('click', () => {
          if (c.dataset.layer === '0') return;
          active = i; render();
        })
      );
    });
  }

  /* ---------- Hero auto-slider ---------- */
  function initHeroSlider() {
    $$('.hero-slider').forEach((hs) => {
      const track = $('.hero-slider__track', hs);
      const dots = $$('.hero-slider__dots button', hs);
      if (!track) return;
      const update = () => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        dots.forEach((d, di) => d.setAttribute('aria-current', di === i ? 'true' : 'false'));
      };
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      dots.forEach((d, di) =>
        d.addEventListener('click', () => track.scrollTo({ left: di * track.clientWidth, behavior: 'smooth' }))
      );
      if (hs.dataset.autoplay === 'true') {
        setInterval(() => {
          const i = Math.round(track.scrollLeft / track.clientWidth);
          const n = (i + 1) % dots.length;
          track.scrollTo({ left: n * track.clientWidth, behavior: 'smooth' });
        }, 5000);
      }
    });
  }

  /* ---------- Grid-density toggle (collection) ---------- */
  function initGridToggle() {
    const toggle = $('.grid-toggle');
    const collection = $('.collection');
    if (!toggle || !collection) return;
    const stored = localStorage.getItem('vitrine:spot') === '1';
    collection.classList.toggle('is-spot', stored);
    toggle.setAttribute('aria-pressed', stored ? 'true' : 'false');
    toggle.addEventListener('click', () => {
      const on = collection.classList.toggle('is-spot');
      toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      try { localStorage.setItem('vitrine:spot', on ? '1' : '0'); } catch (e) {}
    });
  }

  /* ---------- Accordion tabs ---------- */
  function initAccordionTabs(scope = document) {
    $$('.acc', scope).forEach((acc) => {
      const tabs = $$('.acc__tab', acc);
      const panels = $$('.acc__panel', acc);
      tabs.forEach((t, i) =>
        t.addEventListener('click', () => {
          tabs.forEach((x) => x.setAttribute('aria-selected', 'false'));
          panels.forEach((p) => (p.hidden = true));
          t.setAttribute('aria-selected', 'true');
          if (panels[i]) panels[i].hidden = false;
        })
      );
    });
    $$('.faq-item', scope).forEach((item) => {
      const trigger = $('.faq-trigger', item);
      const body = $('.faq-body', item);
      trigger?.addEventListener('click', () => {
        const open = item.classList.toggle('is-open');
        body.style.maxHeight = open ? body.scrollHeight + 'px' : '0px';
      });
    });
    $$('.tabs-nav', scope).forEach((nav) => {
      const btns = $$('.tab-btn', nav);
      const targets = btns.map((b) => document.getElementById(b.getAttribute('aria-controls')));
      btns.forEach((b, i) =>
        b.addEventListener('click', () => {
          btns.forEach((x) => x.setAttribute('aria-selected', 'false'));
          targets.forEach((t) => t && (t.hidden = true));
          b.setAttribute('aria-selected', 'true');
          if (targets[i]) targets[i].hidden = false;
        })
      );
    });
  }

  /* ---------- Predictive search ---------- */
  function initSearch() {
    const input = $('#SearchInput');
    const results = $('#SearchResults');
    if (!input || !results) return;
    let t;
    input.addEventListener('input', () => {
      clearTimeout(t);
      const q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      t = setTimeout(async () => {
        try {
          const url = `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
          const res = await fetch(url);
          results.innerHTML = await res.text();
          initCardCarousels(results);
          paintWishlist();
        } catch (e) { /* noop */ }
      }, 250);
    });
  }

  /* ---------- Cart AJAX + quick add ---------- */
  async function refreshCartCount() {
    try {
      const res = await fetch(`${window.theme.routes.cart_url}.js`);
      const cart = await res.json();
      $$('.header__cart-count').forEach((el) => (el.textContent = cart.item_count));
      const drawer = $('#CartDrawer .drawer__body');
      if (drawer) {
        const html = await (await fetch(`${window.theme.routes.cart_url}?section_id=cart-drawer`)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('#CartDrawer .drawer__body');
        if (fresh) drawer.innerHTML = fresh.innerHTML;
      }
    } catch (e) {}
  }

  function initCart() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      btn && (btn.disabled = true);
      try {
        await fetch(window.theme.routes.cart_add_url, { method: 'POST', body: new FormData(form) });
        await refreshCartCount();
        document.getElementById('CartDrawer')?.classList.add('is-open');
        document.body.classList.add('cust-scroll-lock');
      } finally {
        btn && (btn.disabled = false);
      }
    });

    document.addEventListener('click', async (e) => {
      const buyBtn = e.target.closest('[data-buy-now]');
      if (!buyBtn) return;
      e.preventDefault();
      const form = buyBtn.closest('form');
      if (!form) return;
      buyBtn.disabled = true;
      try {
        await fetch(window.theme.routes.cart_add_url, { method: 'POST', body: new FormData(form) });
        window.location.href = window.theme.routes.checkout_url;
      } catch (err) {
        buyBtn.disabled = false;
      }
    });

    document.addEventListener('click', async (e) => {
      const c = e.target.closest('[data-cart-change]');
      if (!c) return;
      e.preventDefault();
      const line = c.dataset.line;
      const qty = c.dataset.qty;
      await fetch(window.theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(qty) }),
      });
      refreshCartCount();
    });
  }

  function initQuickAdd() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      const handle = btn.dataset.quickAdd;
      const modal = $('#QuickAdd');
      if (!modal) return;
      modal.classList.add('is-open');
      document.body.classList.add('cust-scroll-lock');
      const closeModal = () => { modal.classList.remove('is-open'); document.body.classList.remove('cust-scroll-lock'); };
      modal.onclick = (ev) => {
        if (ev.target === modal || ev.target.closest('[data-drawer-close]')) closeModal();
      };
      const body = $('.qa-modal__body', modal);
      body.innerHTML = '<p style="padding:2vw">…</p>';
      try {
        const html = await (await fetch(`/products/${handle}?section_id=quick-add`)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        body.innerHTML = doc.querySelector('.quick-add-inner')?.innerHTML || '';
        initAccordionTabs(body);
        initProductForm(body);
        paintWishlist();
      } catch (err) {
        body.innerHTML = '<p style="padding:2vw">Unable to load.</p>';
      }
    });
  }

  /* ---------- PDP variant / price ---------- */
  // Scoped to `scope` so a quick-add modal never binds to a different
  // product's <script data-product-json> elsewhere on the same page
  // (e.g. the main PDP's own script, when quick-add is opened from a
  // "related products" grid on a product page).
  function initProductForm(scope = document) {
    $$('script[data-product-json]', scope).forEach((json) => {
      const pdp = json.closest('product-info, .quick-add-inner, body');
      if (!pdp || pdp.dataset.pfBound === 'true') return;
      let product;
      try { product = JSON.parse(json.textContent); } catch (e) { return; }
      pdp.dataset.pfBound = 'true';

      const form = $('form[data-product-form]', pdp) || $('form[action*="/cart/add"]', pdp);
      const idInput = form?.querySelector('input[name="id"]');
      const priceEl = $('[data-price]', pdp);
      const submit = form?.querySelector('[type="submit"]');
      const buyNow = form?.querySelector('[data-buy-now]');
      const optionInputs = $$('.opt-pills input', pdp);
      const readOptions = () => {
        const groups = {};
        optionInputs.forEach((i) => { if (i.checked) groups[i.dataset.optionPosition] = i.value; });
        return product.options.map((_, idx) => groups[idx + 1]);
      };
      const update = () => {
        const chosen = readOptions();
        const variant = product.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
        if (variant && idInput) idInput.value = variant.id;
        if (priceEl) priceEl.innerHTML = variant ? money(variant.price) : priceEl.innerHTML;
        if (submit) {
          submit.disabled = !variant || !variant.available;
          submit.textContent = !variant
            ? 'Unavailable'
            : variant.available
            ? window.theme.strings.addToCart
            : window.theme.strings.soldOut;
        }
        if (buyNow) buyNow.disabled = !variant || !variant.available;
      };
      optionInputs.forEach((i) => i.addEventListener('change', update));
      update();
    });
  }

  /* ---------- Wishlist (client-side only — swap for an app/metafield later) ---------- */
  function initWishlist() {
    const KEY = 'vitrine:wishlist';
    const load = () => {
      try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; }
    };
    const save = (list) => {
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    };
    const paint = () => {
      const list = load();
      $$('[data-wish]').forEach((btn) => {
        const on = list.includes(btn.dataset.wish);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    };
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wish]');
      if (!btn) return;
      e.preventDefault();
      const handle = btn.dataset.wish;
      let list = load();
      list = list.includes(handle) ? list.filter((h) => h !== handle) : list.concat(handle);
      save(list);
      paint();
    });
    paint();
    return paint;
  }

  /* ---------- Real product recommendations (lazy, via Shopify's API) ---------- */
  function initProductRecommendations() {
    if (customElements.get('product-recommendations')) return;
    customElements.define(
      'product-recommendations',
      class extends HTMLElement {
        connectedCallback() {
          const url = this.dataset.url;
          if (!url) return;
          const io = new IntersectionObserver((entries) => {
            if (!entries[0].isIntersecting) return;
            io.disconnect();
            fetch(url)
              .then((r) => (r.ok ? r.text() : ''))
              .then((html) => {
                if (!html) return;
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const fresh = doc.querySelector('product-recommendations');
                if (fresh && fresh.innerHTML.trim()) {
                  this.innerHTML = fresh.innerHTML;
                  initCardCarousels(this);
                  paintWishlist();
                } else if (!this.innerHTML.trim()) {
                  this.remove();
                }
              })
              .catch(() => {});
          });
          io.observe(this);
        }
      }
    );
  }

  /* ---------- PIN widget dismiss ---------- */
  function initPin() {
    const pin = $('.pin-widget');
    if (!pin) return;
    if (sessionStorage.getItem('vitrine:pin') === 'closed') pin.classList.add('is-hidden');
    $('.pin-widget__close', pin)?.addEventListener('click', () => {
      pin.classList.add('is-hidden');
      try { sessionStorage.setItem('vitrine:pin', 'closed'); } catch (e) {}
    });
  }

  /* ---------- boot ---------- */
  let paintWishlist = () => {};
  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initDrawers();
    initMobileMenuAccordions();
    initCardCarousels();
    initDeck();
    initHeroSlider();
    initGridToggle();
    initAccordionTabs();
    initSearch();
    initCart();
    initQuickAdd();
    initProductForm();
    initProductRecommendations();
    initPin();
    paintWishlist = initWishlist();
  });
})();
